// Service for Attendance Export
import { apiClient, API_CONFIG } from '@/api/core';
import type { ApiResponse } from '@/api/shared';
import { buildQueryString } from '@/api/shared';
import type { APIExportFormat, UIExportFormat } from '@/types/fileExport.types';


export class AttendanceExportService {
  async exportDashboard(params: {
    year?: number;
    month?: number;
    monthlyCutId?: number;
    format?: APIExportFormat;
  }): Promise<ApiResponse<{ blob: Blob; filename: string }>> {
    const queryString = buildQueryString(params);

    return apiClient.downloadBlobWithFilename(
      `${API_CONFIG.ENDPOINTS.ATTENDANCE.DASHBOARD_EXPORT()}${queryString}`,
      { method: 'GET' },
    );
  }

  async exportEmployeeAttendance(
    employeeId: number,
    startDate: string,
    endDate: string,
    searchText?: string | null,
  ): Promise<ApiResponse<{ blob: Blob; filename: string }>> {
    const queryString = buildQueryString({
      startDate,
      endDate,
      searchText: searchText || undefined,
    });

    return apiClient.downloadBlobWithFilename(
      `${API_CONFIG.ENDPOINTS.ATTENDANCE.EXPORT_EMPLOYEE_ATTENDANCE(employeeId)}${queryString}`,
      { method: 'GET' },
    );
  }

  async exportMonthlyReport(
    queryType: 'MONTHLY_CUT' | 'DATE_RANGE',
    monthlyCutCode: string | undefined,
    dateFrom: string | undefined,
    dateTo: string | undefined,
    hourFormat: 'HH:MM:SS' | 'DECIMAL' = 'HH:MM:SS',
    exportFormat: UIExportFormat = 'EXCEL',
    filterCompanyId?: number,
  ): Promise<ApiResponse<{ blob: Blob; filename: string }>> {
    // Convert hour format from UI format to API format
    const apiHourFormat = hourFormat === 'HH:MM:SS' ? 'HHMMSS' : 'DECIMAL';

    const queryParams: Record<string, string | undefined> = {
      queryType,
      hourFormat: apiHourFormat,
      exportFormat,
      filterCompanyId: filterCompanyId ? String(filterCompanyId) : undefined,
    };

    if (queryType === 'MONTHLY_CUT' && monthlyCutCode) {
      queryParams.monthlyCutCode = monthlyCutCode;
    } else if (queryType === 'DATE_RANGE' && dateFrom && dateTo) {
      queryParams.dateFrom = dateFrom;
      queryParams.dateTo = dateTo;
    }

    const queryString = buildQueryString(queryParams);

    return apiClient.downloadBlobWithFilename(
      `${API_CONFIG.ENDPOINTS.ATTENDANCE.EXPORT_MONTHLY_REPORT}${queryString}`,
      { method: 'GET' },
    );
  }
}

export const attendanceExportService = new AttendanceExportService();
